/**
 * pickMidlineAnnotation.ts — SW-8943
 *
 * Pure helper used by EditorContainer to keep the sidebar's
 * expanded comment card in sync with whatever annotation the user
 * is currently reading.
 *
 * Selection rule:
 *   1. Among rects that cross the midline (top <= midlineY <= bottom),
 *      pick the one whose vertical center is closest to midlineY. When two
 *      highlights are close, several rects can cross at once; picking the
 *      closest center avoids sticking on a tall upper annotation and
 *      "skipping" a tight second one below it.
 *   2. Crossing ties (same distance): prefer the lower rect on the page
 *      (larger `top`) so the sidebar follows manuscript order.
 *   3. Otherwise pick the rect whose center is closest to midlineY among
 *      non-crossing rects with a known comment, unless a max fallback
 *      distance is provided and the nearest rect is still too far away.
 *   4. Non-crossing ties in distance prefer the later (below-midline) rect so the
 *      sidebar leans toward "what's next" rather than "what was".
 *
 * An annotation span's `data-annotation-id` attribute can be a
 * space-separated list (two comments can share a word, etc.). We
 * pick the first id on the span that resolves to a comment id.
 */

export interface AnnotationRect {
  /**
   * Annotation ids present on this span, parsed from
   * `data-annotation-id` (space-separated).
   */
  annotationIds: string[];
  /** Top of the rect in parent-viewport coordinates. */
  top: number;
  /** Bottom of the rect in parent-viewport coordinates. */
  bottom: number;
}

export interface PickMidlineAnnotationOptions {
  /**
   * Maximum allowed center distance for the non-crossing nearest-neighbor
   * fallback. When omitted, any distance is allowed.
   */
  maxFallbackDistance?: number;
}

/**
 * Parse a `data-annotation-id` attribute value into a list of
 * trimmed, non-empty ids. `null` / empty string / whitespace-only
 * attributes produce `[]`.
 */
export function parseAnnotationIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(/\s+/).filter((id) => id.length > 0);
}

/**
 * Given a rect's annotation ids and a map of annotationId -> commentId,
 * return the first id on the rect that has a matching comment — or null.
 */
function firstMatchingCommentId(
  rect: AnnotationRect,
  commentByAnnotationId: Map<string, string>
): string | null {
  for (const id of rect.annotationIds) {
    const commentId = commentByAnnotationId.get(id);
    if (commentId) return commentId;
  }
  return null;
}

export function pickMidlineAnnotationId(
  rects: AnnotationRect[],
  midlineY: number,
  commentByAnnotationId: Map<string, string>,
  options: PickMidlineAnnotationOptions = {}
): string | null {
  if (!rects || rects.length === 0) return null;
  if (commentByAnnotationId.size === 0) return null;

  // Pass 1: crossing rects — choose best by center distance to midline, not
  // first in DOM order (see module docstring).
  let bestCrossCommentId: string | null = null;
  let bestCrossDistance = Infinity;
  let bestCrossTopForTie = -Infinity;

  for (const r of rects) {
    if (r.top <= midlineY && midlineY <= r.bottom) {
      const commentId = firstMatchingCommentId(r, commentByAnnotationId);
      if (!commentId) continue;

      const center = r.top + (r.bottom - r.top) / 2;
      const distance = Math.abs(center - midlineY);

      if (distance < bestCrossDistance) {
        bestCrossDistance = distance;
        bestCrossCommentId = commentId;
        bestCrossTopForTie = r.top;
      } else if (distance === bestCrossDistance && r.top > bestCrossTopForTie) {
        bestCrossCommentId = commentId;
        bestCrossTopForTie = r.top;
      }
    }
  }

  if (bestCrossCommentId !== null) {
    return bestCrossCommentId;
  }

  // Pass 2: nearest center. Track the best candidate.
  let bestCommentId: string | null = null;
  let bestDistance = Infinity;
  let bestIsBelow = false;

  for (const r of rects) {
    const commentId = firstMatchingCommentId(r, commentByAnnotationId);
    if (!commentId) continue;

    const center = r.top + (r.bottom - r.top) / 2;
    const distance = Math.abs(center - midlineY);
    const isBelow = center >= midlineY;

    if (distance < bestDistance) {
      bestDistance = distance;
      bestCommentId = commentId;
      bestIsBelow = isBelow;
    } else if (distance === bestDistance && isBelow && !bestIsBelow) {
      // Tie-break: prefer the below-midline candidate.
      bestCommentId = commentId;
      bestIsBelow = true;
    }
  }

  if (
    bestCommentId !== null &&
    typeof options.maxFallbackDistance === "number" &&
    bestDistance > options.maxFallbackDistance
  ) {
    return null;
  }

  return bestCommentId;
}
